import { PakPricingUpdateInput, TaskStatus } from '@cpak/adp-cpak-pricing-service-shared';
import { Injectable } from '@angular/core';
import { EMPTY, expand, map, Observable, switchMap, takeLast, timer } from 'rxjs';
import { FetcherService } from './fetcher/fetcher.service';
import {
  CheckPakOwnershipResponseData,
  GetQuoteByIdResponsePayload,
  GetRelationsByQuoteResponsePayload,
  SalesforceSendPakResponsePayload,
} from '@cpak/adp-cpak-bff-service-models';
import {
  RelationTypeEnum,
  CreateQuoteInput,
  CreateFromAnotherOrUpdateQuoteInput,
  GetTaskProcessResponsePayload,
} from '@cpak/adp-cpak-quotes-service-models';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class QuoteApiService {
  constructor(private fetcher: FetcherService, private http: HttpClient) {}

  private getTaskProcessById(id: string): Observable<GetTaskProcessResponsePayload | null> {
    return this.fetcher.get(`/api/quotes/taskProcess/${id}`);
  }

  public pollTaskProcess(id: string): Observable<GetTaskProcessResponsePayload | null> {
    return timer(2000).pipe(
      switchMap(() => this.getTaskProcessById(id)),
      expand(response =>
        response?.data?.taskProcess && response.data.taskProcess['status'] !== TaskStatus.completed
          ? timer(2000).pipe(switchMap(() => this.getTaskProcessById(id)))
          : EMPTY
      ),
      takeLast(1)
    );
  }

  public getQuoteData(id: string): Observable<GetQuoteByIdResponsePayload | null> {
    return this.fetcher.get(`/api/pages/quotes/${id}`);
  }

  public updateQuoteById(
    quoteId: string,
    quoteData?: CreateFromAnotherOrUpdateQuoteInput,
    pakPricingModel?: PakPricingUpdateInput
  ): Observable<GetQuoteByIdResponsePayload | null> {
    if (quoteData?.customerDescription === '') delete quoteData.customerDescription;
    const body = { pricing: pakPricingModel, quote: quoteData };

    return this.fetcher.patch(`/api/pages/quotes/${quoteId}`, body);
  }

  public createQuote(
    relation: RelationTypeEnum,
    quoteId: string,
    data: CreateFromAnotherOrUpdateQuoteInput
  ): Observable<GetQuoteByIdResponsePayload | null> {
    if (data.customerDescription === '') delete data.customerDescription;
    return this.fetcher.put(`/api/pages/quotes/${quoteId}/create?relation=${relation}`, data);
  }

  public deleteQuote(quoteId: string): Observable<GetQuoteByIdResponsePayload | null> {
    return this.fetcher.delete(`/api/pages/quotes/${quoteId}`);
  }

  public createQuoteFromScratch(data: CreateQuoteInput): Observable<GetQuoteByIdResponsePayload | null> {
    return this.fetcher.put(`/api/pages/quotes`, data);
  }

  public getRelationsById(id: string): Observable<GetRelationsByQuoteResponsePayload | null> {
    return this.fetcher.get(`/api/pages/quotes/${id}/relations`);
  }

  public checkPackOwnership(packId: string): Observable<CheckPakOwnershipResponseData | null> {
    return this.fetcher.get<{ data: CheckPakOwnershipResponseData }>(`/api/packs/${packId}/checkOwnership`).pipe(
      map(response => {
        if (response === null) {
          return null;
        }
        return response.data;
      })
    );
  }

  public saveToSalesforce(
    pakId: string,
    userId: string,
    culture?: string
  ): Observable<SalesforceSendPakResponsePayload | null> {
    const headers = new HttpHeaders({
      'X-User-Id': userId,
      'Accept-Language': culture || 'en-US',
    });

    const data = {
      id: pakId,
    };

    return this.http.post<SalesforceSendPakResponsePayload | null>(`api/pages/quotes/salesforce/send/${pakId}`, data, {
      headers,
    });
  }
}
